import React, {useRef} from 'react';

import SearchModel from 'coreClasses/search/SearchModel';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useIntersection, useSearchMgrInstance, useSetUI} from 'coreHooks';
import useChangeEffect from 'coreHooks/useChangeEffect';

import {NEXT_DIRECTION, PREV_DIRECTION} from 'brand/app/constants';

import {IScrollMoreProps} from './IScrollMore';

import styles from './ScrollMore.module.scss';

const ScrollMore = ({
	direction,
	searchInstanceKey,
	searchType,
}: IScrollMoreProps) => {
	const searchMgr = useSearchMgrInstance({
		searchType,
		key: searchInstanceKey,
	});
	const scrollRef = useRef(null);
	const searchModel: SearchModel = searchMgr.getStoredSearchModel();
	const setUI = useSetUI();

	const lowestPage = searchModel.getLowestLoadedPageNumber();
	const entry = useIntersection(scrollRef);
	// `useIntersection` gives a new entry at every scroll while the sentinel is visible: one search at a time, or the
	// same page is requested once per scroll event until the first answer arrives.
	const isLoadingRef = useRef(false);

	// On changes only: a PLP kept alive (`<Activity>`) shown again must not load again with the entry it was left with.
	useChangeEffect(() => {
		const handleSearch = (direction, lowestPage) => {
			if (direction === PREV_DIRECTION) {
				return searchMgr.search({page: lowestPage - 1});
			} else if (searchMgr.loadPage(direction)) {
				return searchMgr.search();
			} else {
				return Promise.resolve(null);
			}
		};

		if (entry?.isIntersecting && !isLoadingRef.current) {
			const heightBefore = document.querySelector('main').scrollHeight;

			isLoadingRef.current = true;

			handleSearch(direction, lowestPage).finally(() => {
				isLoadingRef.current = false;
			}).then(() => {
				if (direction === NEXT_DIRECTION) {
					return;
				}

				const heightAfter = document.querySelector('main').scrollHeight;
				const scrollOffset = heightAfter - heightBefore;

				setUI.setHeader({locked: true});
				ViewportMgr.scrollTo(scrollOffset);
			});
		}
	}, [entry]);

	return (
		<div className={styles['scroll-more']} ref={scrollRef} />
	);
};

export default React.memo(ScrollMore, (prevProps, nextProps) => {
	return prevProps.direction === nextProps.direction &&
		prevProps.searchInstanceKey === nextProps.searchInstanceKey &&
		prevProps.searchType === nextProps.searchType;
});
